import { Link } from 'react-router-dom';
import useDocumentTitle from '../hooks/useDocumentTitle';

function Privacy() {
  useDocumentTitle('Privacy Policy — MMI');

  return (
    <main className="legal-page">
      <article className="legal-document">
        <header className="legal-header">
          <p className="legal-updated">Last updated — May 2026</p>
          <h1>Privacy Policy</h1>
          <p className="legal-intro">
            MMI builds agentic AI systems, immersive AR/VR experiences, and intelligent Meta
            applications. That work involves data — yours, your users&apos;, and the signals our
            systems need to function well. This policy explains what we collect, why we collect it,
            and how we keep it handled responsibly.
          </p>
        </header>

        <section className="legal-section">
          <h2>What we build — and why privacy matters here</h2>
          <p>
            Our work spans autonomous AI agents that act on your behalf, spatial computing
            environments that may capture interaction and telemetry, and automation pipelines that
            connect to your existing tools. Each layer has different data implications. We design
            with privacy in mind from architecture onward — not as an afterthought before launch.
          </p>
        </section>

        <section className="legal-section">
          <h2>Information we collect</h2>
          <p>Depending on how you engage with MMI, we may collect:</p>
          <ul>
            <li>
              <strong>Contact details</strong> — name, email, phone,
              company, and message content when you reach out via our contact form or email.
            </li>
            <li>
              <strong>Project and technical data</strong> — requirements,
              assets, API credentials (stored securely), and integration metadata needed to deliver
              Agentic AI, AR/VR, or Meta application work.
            </li>
            <li>
              <strong>Agentic AI interaction data</strong> — prompts,
              agent decisions, tool calls, and feedback loops when we deploy or test autonomous
              systems on your behalf. Scope is defined per project.
            </li>
            <li>
              <strong>Immersive / AR/VR data</strong> — session
              telemetry, device types, performance metrics, and optionally anonymized usage patterns
              in simulations or training environments. We do not sell spatial or behavioral profiles.
            </li>
            <li>
              <strong>Website usage</strong> — basic analytics such as
              pages visited, referrer, and browser type, if enabled. We keep this minimal.
            </li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>How we use your information</h2>
          <p>We use collected data to:</p>
          <ul>
            <li>Respond to inquiries and deliver the services you hire us for</li>
            <li>Design, train, fine-tune, and operate AI agents and immersive systems under agreed scope</li>
            <li>Improve reliability, safety, and performance of models and experiences we maintain for you</li>
            <li>Communicate about project status, support, and relevant product updates</li>
            <li>Meet legal obligations and protect against misuse or security threats</li>
          </ul>
          <p>
            We do not use your proprietary data to train general-purpose models for other clients
            unless you explicitly agree in writing.
          </p>
        </section>

        <section className="legal-section">
          <h2>Third-party services</h2>
          <p>
            Our contact form is processed through <strong>Web3Forms</strong>.
            When you submit the form, your message is transmitted to Web3Forms so we can receive it
            by email. Their handling of that submission is governed by their own privacy practices.
          </p>
          <p>
            Project work may also involve cloud providers, AI model APIs, analytics tools, or
            deployment platforms you approve. We document these in project agreements when they
            apply.
          </p>
        </section>

        <section className="legal-section">
          <h2>Retention and security</h2>
          <p>
            We retain data only as long as needed for delivery, support, or legal requirements.
            Agent logs, immersive session data, and project artifacts are stored with access controls
            appropriate to sensitivity. No system is perfectly secure — but we apply industry-standard
            practices and restrict access to people who need it.
          </p>
        </section>

        <section className="legal-section">
          <h2>Your rights</h2>
          <p>You may request to:</p>
          <ul>
            <li>Access the personal data we hold about you</li>
            <li>Correct inaccurate information</li>
            <li>Delete data where we are not legally required to retain it</li>
            <li>Withdraw consent for optional processing</li>
            <li>Receive a copy of your data in a portable format, where feasible</li>
          </ul>
          <p>
            We will respond to legitimate requests within a reasonable timeframe. Some requests may
            be limited where they affect other clients&apos; confidentiality or active project integrity.
          </p>
        </section>

        <section className="legal-section">
          <h2>Changes to this policy</h2>
          <p>
            We may update this policy as our services evolve — especially as agentic AI and spatial
            computing capabilities expand. Material changes will be reflected on this page with an
            updated date.
          </p>
        </section>

        <div className="legal-contact">
          <p>
            Questions about privacy? Email{' '}
            <a href="mailto:hello@mmi.com">hello@mmi.com</a> or visit our{' '}
            <Link to="/contact">contact page</Link>.
          </p>
        </div>
      </article>
    </main>
  );
}

export default Privacy;
